/**
 * The Seasonality screen's pure half (`docs/features/seasonality.md`,
 * mock `docs/seasonality-layout-mock.html`): defaults, what Basic may set, the
 * chip labels, the start-date text and the calendar's cells. No React, no fetch.
 */

import type { ScreenerSeasonality } from "@qufolio/model";

import type { SeasWindow } from "../api/seasonality";

/** Pro (group `qf-seas-pro`): everything, and implies Basic (owner, 2026-10-22). */
export const PRO_CAP = "ind:SeasonalityPro";
/** Basic (group `qf-seasonality`): the screen on D, Trade, Hit and Avg; `seasBands`. */
export const BASIC_CAP = "ind:Seasonality";

export type Tier = "pro" | "basic" | null;

export function tierOf(can: (capability: string) => boolean): Tier {
  if (can(PRO_CAP)) return "pro";
  if (can(BASIC_CAP)) return "basic";
  return null;
}

export function newSeasonality(base: string): ScreenerSeasonality {
  return { base, mode: "soon", basis: "d", years: "10", dir: "both", rank: "season", hitMin: 80, avgMin: 5, within: 10 };
}

/** The settings a tier is answered with — Basic is D and Trade whatever was saved. */
export function effective(
  seas: ScreenerSeasonality,
  tier: Tier,
): Required<Omit<ScreenerSeasonality, "from" | "periodMin" | "periodMax">> & {
  from?: string;
  periodMin?: number;
  periodMax?: number;
} {
  const pro = tier === "pro";
  return {
    base: seas.base,
    mode: seas.mode ?? "soon",
    basis: pro ? (seas.basis ?? "d") : "d",
    years: seas.years ?? "10",
    dir: seas.dir ?? "both",
    rank: pro ? (seas.rank ?? "season") : "trade",
    hitMin: seas.hitMin ?? 80,
    avgMin: seas.avgMin ?? 0,
    minYears: seas.minYears ?? 5,
    within: seas.within ?? 10,
    // Basic is D only, so a W/M range saved by a Pro reader means nothing there.
    ...(seas.periodMin && (pro || (seas.basis ?? "d") === "d") ? { periodMin: seas.periodMin } : {}),
    ...(seas.periodMax && (pro || (seas.basis ?? "d") === "d") ? { periodMax: seas.periodMax } : {}),
    ...(seas.from ? { from: seas.from } : {}),
  };
}

export const UNIT: Record<string, string> = { d: "td", w: "w", m: "m", e: "td" };
const DIR_LABEL: Record<string, string> = { both: "L+S", L: "Long", S: "Short" };
const YEARS_LABEL = (y: string) => (y === "all" ? "All y" : `${y}y`);

/** The Period range the search tries, per basis (owner, 2026-10-22). */
export const PERIOD_RANGE: Record<string, [number, number]> = { d: [5, 60], w: [2, 12], m: [1, 6] };

/** `20–40td`, or empty when the range is the whole search. */
export function periodLabel(seas: ReturnType<typeof effective>): string {
  const [lo, hi] = PERIOD_RANGE[seas.basis] ?? [1, 260];
  const a = seas.periodMin ?? lo;
  const b = seas.periodMax ?? hi;
  if (a <= lo && b >= hi) return "";
  return `${a}–${b}${UNIT[seas.basis] ?? "td"}`;
}

/** `D · 10y · L+S · Season`, and the Period when narrowed: `D · 20–40td · 10y …` */
export function settingsChip(seas: ReturnType<typeof effective>, tier: Tier): string {
  const period = periodLabel(seas);
  const parts = [seas.basis.toUpperCase(), ...(period ? [period] : []), YEARS_LABEL(seas.years), DIR_LABEL[seas.dir] ?? seas.dir];
  if (tier === "pro") parts.push(seas.rank === "season" ? "Season" : "Trade");
  return parts.join(" · ");
}

/** `Hit 80% · Avg 5% · ≥5y` */
export function floorsChip(seas: ReturnType<typeof effective>): string {
  return `Hit ${fmtNum(seas.hitMin)}% · Avg ${fmtNum(seas.avgMin)}% · ≥${seas.minYears}y`;
}

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** An ISO date as `Oct 22`, read as a calendar day (never shifted by the zone). */
export function shortDate(iso: string): string {
  const [, m, d] = iso.split("-").map(Number);
  return `${MON[(m ?? 1) - 1]} ${d}`;
}

function dayOf(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1));
}

/**
 * `Oct 22 +10td`, or for Moving now / Calendar just the date. Within is
 * trading days on every basis — the server counts sessions from the start date.
 */
export function startChip(seas: ReturnType<typeof effective>, used: string | null): string {
  const date = used ?? seas.from ?? null;
  const when = date ? shortDate(date) : "Today";
  if (seas.mode !== "soon") return when;
  return `${when} +${seas.within}td`;
}

/**
 * What the bar says when the start date was rolled: `weekend → Mon Oct 26`,
 * `market closed → Fri Nov 27`. Null when it was used as given.
 */
export function rollNote(asked: string, used: string, rolled: boolean): string | null {
  if (!rolled) return null;
  const a = dayOf(asked).getUTCDay();
  const why = a === 0 || a === 6 ? "weekend" : "market closed";
  return `${why} → ${DOW[dayOf(used).getUTCDay()]} ${shortDate(used)}`;
}

/** The Start cell: `Oct 26 +2`, `Oct 22`, or `Oct 15 d6` for a window already running. */
export function startCell(w: SeasWindow): { date: string; note: string } {
  const date = w.startDate ? shortDate(w.startDate) : "";
  if (w.offset > 0) return { date, note: `+${w.offset}` };
  if (w.offset === 0) return { date, note: "" };
  return { date, note: `d${1 - w.offset}` };
}

export type SortKey = "symbol" | "dir" | "offset" | "len" | "hit" | "edge" | "xsHit" | "xsSharpe";

export function sortWindows(rows: readonly SeasWindow[], key: SortKey, dir: 1 | -1): SeasWindow[] {
  const out = [...rows];
  out.sort((a, b) => {
    const x = a[key];
    const y = b[key];
    if (typeof x === "string" || typeof y === "string") return String(x ?? "").localeCompare(String(y ?? "")) * dir;
    if (x == null) return 1;
    if (y == null) return -1;
    // Avg sorts by size, a short's −6 above a long's +4 (the mock's rule).
    const ax = key === "edge" ? Math.abs(x) : x;
    const ay = key === "edge" ? Math.abs(y) : y;
    return (ax - ay) * dir;
  });
  return out;
}

export interface CalendarCell {
  date: string;
  isFrom: boolean;
  windows: SeasWindow[];
}

/**
 * Four weeks of weekdays from the Monday of the start date's week, each with the
 * windows that start on it. A holiday shows as an ordinary empty day.
 */
export function calendarCells(from: string, rows: readonly SeasWindow[]): CalendarCell[] {
  const start = dayOf(from);
  const back = (start.getUTCDay() + 6) % 7;
  start.setUTCDate(start.getUTCDate() - back);
  const byDate = new Map<string, SeasWindow[]>();
  for (const w of rows) {
    if (!w.startDate) continue;
    const list = byDate.get(w.startDate) ?? [];
    list.push(w);
    byDate.set(w.startDate, list);
  }
  const cells: CalendarCell[] = [];
  for (let week = 0; week < 4; week++) {
    for (let day = 0; day < 5; day++) {
      const d = new Date(start);
      d.setUTCDate(start.getUTCDate() + week * 7 + day);
      const iso = d.toISOString().slice(0, 10);
      cells.push({ date: iso, isFrom: iso === from, windows: byDate.get(iso) ?? [] });
    }
  }
  return cells;
}

function fmtNum(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}
